/**
 * A password box with an eye on it.
 *
 * ASKED FOR BY NAME: "the password field in all login dialogs needs to enable a visibility toggle
 * to allow the user to see their password in plain text". A password typed blind is retyped blind,
 * and a signature dialog that refuses the second time tells you nothing about which of the two
 * attempts was wrong.
 *
 * A COMPONENT RATHER THAN SIX PATCHES. Every place this replaces had built its own `type="password"`
 * TextField, so a toggle added by hand would have been six slightly different toggles -- different
 * icon sizes, some of them in the tab order, one of them inevitably without the aria-label. The
 * props pass straight through, so a caller keeps its own label, helper text and autoComplete.
 *
 * slotProps, NOT InputProps. MUI 9 deprecated the old shape, and Login2 -- the sign-in screen
 * this copies, which is the suite's oldest password toggle -- already moved. An adornment passed
 * the deprecated way still renders, so the mistake would have shown up as a console warning in
 * three repos rather than as anything visible.
 *
 * THE EYE IS OUT OF THE TAB ORDER. Tab from the password goes to Sign, not to a button whose job
 * is to undo the field's own masking -- somebody typing their password and pressing Tab,Enter
 * should sign, not reveal.
 *
 * SHOWING IS THE READER'S CHOICE AND IT ENDS WITH THE DIALOG. The state lives here and the
 * component unmounts with its dialog, so a field reopened is a field masked again; nothing is
 * remembered and nothing is stored.
 */
import React, {useState} from "react";
import {IconButton, InputAdornment, TextField, TextFieldProps} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";

export default function PasswordField(props: TextFieldProps) {
    const [shown, setShown] = useState(false);

    return (
        <TextField
            {...props}
            type={shown ? "text" : "password"}
            slotProps={{
                ...props.slotProps,
                input: {
                    ...(props.slotProps?.input as object),
                    endAdornment: (
                        <InputAdornment position="end">
                            {/* onMouseDown preventDefault so pressing the eye does not pull focus
                                out of the field and drop the caret; the icon shows the state the
                                field is IN, which is how every other toggle in the suite reads. */}
                            <IconButton onClick={() => setShown(one => !one)}
                                        onMouseDown={event => event.preventDefault()}
                                        edge="end" size="small" tabIndex={-1}
                                        aria-label={shown ? "Hide password" : "Show password"}>
                                {shown ? <Visibility fontSize="small"/>
                                       : <VisibilityOff fontSize="small"/>}
                            </IconButton>
                        </InputAdornment>
                    ),
                },
            }}/>
    );
}
